import { useRouteMatch } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import AsyncTableContainer from '../Table/AsyncTableContainer';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import { isAdmin, isPM } from '../../utils';
import { getExportTitle } from '../ViewExport/utils';
import { getAmenityBaseUrl } from './utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import { TABLE_DATA_TYPES } from '../../staticData';
import { history } from '../../store';
import StyledLink from '../StyledLink';
import ListActions from '../Table/ListActions';
import { buildingFilter, limitFilter } from '../Table/filters';
import Tooltip from '../Tooltip';
import AmenitiesExport from './AmenitiesExport';

const AmenitiesComponent = (props) => {
  const { t } = useTransl();
  const theme = useTheme();
  const { url } = useRouteMatch();
  const baseUrl = getAmenityBaseUrl(url);
  const {
    pmcId,
    buildingId,
    items,
    getItems,
    listKey,
    listParams,
    buildings,
    onLoad,
    onAmenityStatusUpdate,
    onExport,
    pmc,
    building,
    match,
  } = props;

  const canEdit = isAdmin() || isPM(match?.params);

  const columns = [
    {
      Header: t('title.amenity'),
      id: 'name',
      accessor: ({ name, images }) => (
        <Box display="flex" alignItems="center">
          {images?.[0] && (
            <Box mr="1rem">
              <ExpandableImageComponent
                photo={images[0]}
                caption={name}
                height="3.2rem"
                isSquare
              />
            </Box>
          )}
          <Typography>{name}</Typography>
        </Box>
      ),
      sortable: false,
    },
    {
      Header: t('title.building'),
      id: 'building',
      accessor: ({ building: { id, name } }) => (
        <StyledLink to={`/building/${id}`}>{name}</StyledLink>
      ),
      headerClassName: 'sort',
    },
    {
      Header: t('title.status'),
      id: 'status',
      accessor: ({ id, active, name, upcomingReservationsCount }) => {
        const action = [
          {
            label: active ? t('amenity.active') : t('amenity.inactive'),
            isFullWidth: true,
            onClick: () =>
              canEdit
                ? onAmenityStatusUpdate({
                    id,
                    active: !active,
                    name,
                    listParams,
                    upcomingReservationsCount,
                  })
                : null,
            color: active
              ? theme.palette.success.main
              : theme.palette.grey[400],
            disabled: !canEdit,
          },
        ];
        if (canEdit) {
          const toolTipText = active
            ? t('amenity.clickToDeactivate')
            : t('amenity.clickToActivate');
          return (
            <Tooltip tooltip={toolTipText}>
              <ListActions actions={action} />
            </Tooltip>
          );
        }
        return <ListActions actions={action} />;
      },
      width: 130,
      headerClassName: 'sort',
    },
  ];

  const filters = [
    buildingFilter(listParams, buildings, { unit: null }),
    limitFilter(listParams),
  ];

  if (buildingId) {
    filters.splice(0, 1);
  }

  const action = [
    {
      label: t('amenity.createAmenity'),
      url: `${baseUrl}/management/create`,
      show: canEdit,
    },
  ];

  const onRowClick = (rowInfo) => {
    const id = rowInfo?.original?.id;
    id && history.push(`${baseUrl}/management/info/${id}`);
  };

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  const defaultParams = {
    ordering: 'createdAt',
    building: buildingId,
    pmc: pmcId,
  };
  const tableDataType = TABLE_DATA_TYPES.items;

  const title = t('title.amenities');
  const exportTitle = getExportTitle(title, pmc, building);
  const exportActions = (
    <AmenitiesExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableContainer
      tableId={TABLE_ID.amenities}
      title={title}
      items={items}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.created_at}
      defaultSortDesc={false}
      manual
      filters={filters}
      onLoad={onLoad}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      action={<ListActions actions={action} />}
      onRowClick={onRowClick}
      rowStyles={rowStyles}
      showFiltersAreApplied={false}
      exportActions={exportActions}
    />
  );
};

AmenitiesComponent.defaultProps = {
  pmcId: null,
  buildingId: null,
  pmc: null,
  building: null,
  buildings: {},
};

AmenitiesComponent.propTypes = {
  pmcId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  buildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  items: PropTypes.instanceOf(Object).isRequired,
  getItems: PropTypes.func.isRequired,
  listKey: PropTypes.string.isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object),
  onLoad: PropTypes.func.isRequired,
  onAmenityStatusUpdate: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  pmc: PropTypes.instanceOf(Object),
  building: PropTypes.instanceOf(Object),
  match: PropTypes.instanceOf(Object).isRequired,
};

export default AmenitiesComponent;
